import React from "react";
import { ActivityIndicator, Dimensions, ScrollView } from "react-native";
import { defineMessages } from "react-intl";
import { Mutation } from "react-apollo";
import { FormSpy } from "react-final-form";
import {
  Button,
  ConfirmButton,
  ListInspectContext,
  Container,
  ToastContext
} from "cci-component";
import { SecuredComponent } from "cci-logic";
import { withRouter } from "cci-utility";
import ImmutableInspect from "./ImmutableInspect";
import AutoSaveComponent from "./AutoSaveComponent";

const messages = defineMessages({
  delete: {
    id: "delete",
    defaultMessage: "Delete"
  },

  save: {
    id: "save",
    defaultMessage: "Save"
  },

  savedSuccessfully: {
    id: "savedSuccessfully",
    defaultMessage: "Information saved succesfully!"
  },

  confirmDelete: {
    id: "confirmDelete",
    defaultMessage: "Are you sure you want to delete this information?"
  },

  deletedSuccessfully: {
    id: "deletedSuccesfully",
    defaultMessage: "Information deleted successfully!"
  },

  cannotDeleteError: {
    id: "cannotDeleteError",
    defaultMessage:
      "This record cannot be deleted, this is most likely because the record is in use."
  },

  genericError: {
    id: "genericError",
    defaultMessage: "An error has occurred: {message}"
  },

  userDeleteSystemRecord: {
    id: "userDeleteSystemRecord",
    defaultMessage: "This record cannot be deleted, this is a system record."
  },

  autoSave: {
    id: "autoSave",
    defaultMessage: "Auto Save"
  }
});

/**
 * Mutable version of the list component that on top of the features included in ImmutableList, allows the user to change the data
 * by adding new records (add button on the bottom left of the screen).
 *
 * Queries are refetched whenever a record is created or deleted. We determine which queries to refetch by using the ListInspectContext as well as
 * using any query specified in the "refetchQueries" prop.
 *
 * Props:
 * - rootPath: URL to redirect the user when a record has been deleted. Also used to redirect the user when a new record has been created, the new primary key appended to the rootPath.
 * - entityName: Name of the entity that this inspect handles. Used to determine permissions to create, update or delete.
 * - recordId: Id of the record that must be loaded for inspection. Defaults to the "id" param in the react-router URL.
 * - deleteMutation: Mutation used to delete the current record. The record id is passed in with the "id" variable.
 * - onDeleteCompleted: Called when the record has been successfully deleted.
 * - onDeleteError: Called when the deleting the record failed, the server response is passed as the first parameter.
 * - onCompleted: Called when saving the record completed, the server response is passed as the first parameter.
 * - refetchQueries: Array of additional queries that must be refetched whenever a record is deleted or created.
 * - footerAddedComponent: React Component which is added at the bottom of the inspect.
 * - disableSaveChanges: Function which must return boolean. Return true to disable Save and Delete buttons, default return is false.
 * - blockRedirection: Set to true when you want to block history redirection.
 * - saveMessage: Intl message for the save button.
 * - deleteVariables: Function used to return an especific object to delete on server. form is a parameter.
 * - autoSaveEnabled: Boolean, set to true to enable auto save after 5 seconds of no touching/editing fields.
 * - beforeAutoSave: Function tu run before HandleSubmit on AutoSave Component.
 */
export class MutableInspect extends React.Component {
  constructor() {
    super();

    this.state = {
      loadingSave: false
    };
  }

  render() {
    const {
      match,
      history,
      rootPath,
      entityName,
      recordId = match.params.id || this.props.myId,
      deleteMutation,
      onDeleteCompleted = () => { },
      onDeleteError,
      onCompleted = () => { },
      onError = () => { },
      onDeleteCheck = () => { },
      refetchQueries,
      children,
      onPressMock,
      footerAddedComponent: FooterAddedComponent,
      disableSaveChanges = () => false,
      blockRedirection = false,
      saveMessage,
      hideSaveButton = false,
      deleteVariables = () => null,
      autoSaveEnabled = false,
      beforeAutoSave = () => { },
      checkDeleted = false,
      validationContext = {},
      ...restProps
    } = this.props;

    return (
      <ToastContext.Consumer>
        {toast => (
          <ListInspectContext.Consumer>
            {({ getRefetchQuery }) => {
              // Array of query in context (if any) and specific refetchQueries set in the props (if any).
              const refetchAllQueries = [getRefetchQuery(), ...refetchQueries];

              return (
                <ImmutableInspect
                  {...restProps}
                  refetchQueries={refetchAllQueries}
                  // Record was saved successfully
                  onCompleted={(savedData, form) => {
                    // Let the user know the data was saved successfully
                    savedData && toast.success(messages.savedSuccessfully);
                    this.setState({ loadingSave: false });

                    // Mock for testing
                    // TODO Handle this in a better manner
                    onPressMock && onPressMock(savedData);

                    onCompleted(savedData, form);

                    // Redirect the user to the record inspect (useful when creating a new record)
                    if (!blockRedirection) {
                      history.replace(`${rootPath}/${savedData.id}`);
                    }
                  }}
                  // A problem occurred saving the record
                  onError={errors => {
                    this.setState({ loadingSave: false });
                    toast.error(errors);
                    onError(errors)
                  }}
                  setLoading={bool => this.setState({ loadingSave: bool })}
                  fixedFooter={({ handleSubmit, pristine }) => (
                    /* FormSpy is only used to determine the version of the record being inspected, not thrilled with this solution. We might be able to clean this up a bit more */
                    <FormSpy>
                      {({ form }) => (
                        <ScrollView
                          contentContainerStyle={{
                            flex: 1,
                            flexDirection: "row",
                            justifyContent:
                              Dimensions.get("window").width > 970 ? "flex-end" : "flex-start"
                          }}
                          horizontal
                        >

                          {/* AutoSave component */}
                          <AutoSaveComponent
                            {...{ form, handleSubmit, autoSaveEnabled, beforeAutoSave }}
                            validator={restProps.validationSchema}
                            setLoading={bool => {
                              this.setState({ loadingSave: bool });
                            }}
                          />

                          {/* Delete button */}
                          {deleteMutation !== undefined && recordId !== "new" && (
                            <SecuredComponent
                              checkPermissions={{
                                objects: entityName,
                                actions: ["delete"]
                              }}
                            >
                              <Mutation
                                mutation={deleteMutation}
                                variables={
                                  deleteVariables(form) || {
                                    [restProps.objectWrapper]: {
                                      id: recordId,
                                      version: form.getState().values.version
                                    }
                                  }
                                }
                                refetchQueries={refetchAllQueries}
                                onCompleted={data => {
                                  if (checkDeleted) {
                                    onDeleteCheck(data, toast);
                                  } else {
                                    // Record was deleted successfully
                                    toast.success(messages.deletedSuccessfully);

                                    // Redirect the user to the root path since the record no longer exists and they should not be allowed to make changes
                                    history.replace(rootPath);

                                    onDeleteCompleted();
                                  }
                                }}
                                onError={errors => {
                                  // TODO This is an unsafe way to check if we have a SystemRecord issue, this must be solved when DGV-109 is addressed
                                  let deleteSystemRecord = false;
                                  errors.graphQLErrors &&
                                    errors.graphQLErrors.some(error => {
                                      if (
                                        error.exception.cause.message.indexOf(
                                          "User can not delete a System Record"
                                        ) > -1
                                      ) {
                                        deleteSystemRecord = true;
                                        return true;
                                      }
                                      return false;
                                    });
                                  if (errors.toString().indexOf("500") > -1) {
                                    // TODO This is an unsafe way to check if we have a foreign key constraint issue, this must be solved when DGV-109 is addressed
                                    toast.error(messages.cannotDeleteError);
                                  } else if (deleteSystemRecord) {
                                    toast.error(
                                      messages.userDeleteSystemRecord
                                    );
                                  } else {
                                    // A problem occurred delete the record
                                    toast.error(messages.genericError, {
                                      message: errors
                                    });
                                  }

                                  onDeleteError && onDeleteError(error);
                                }}
                              >
                                {deleteHandler => (
                                  <ConfirmButton
                                    ui="dangerLink"
                                    title={messages.delete}
                                    disabled={disableSaveChanges(form)}
                                    message={messages.confirmDelete}
                                    margins="0 auto 0 0"
                                    paddings="0 10px 0 0"
                                    onConfirm={deleteHandler}
                                  />
                                )}
                              </Mutation>
                            </SecuredComponent>
                          )}

                          {/* Bottom Components from props. This is used to add Buttons at the bottom of the page. */}
                          {FooterAddedComponent && (
                            <FooterAddedComponent
                              form={form}
                              handleSubmit={handleSubmit}
                              pristine={pristine}
                              loadingSave={this.state.loadingSave}
                              setSaving={(bool = true) =>
                                this.setState({ loadingSave: bool })
                              }
                            />
                          )}

                          {/* Save Button or ActivityIndicator */}
                          {this.state.loadingSave && !form.getState().submitFailed ? (
                            <Container paddings="12px 50px">
                              <ActivityIndicator animating />
                            </Container>
                          ) : (
                            <SecuredComponent
                              checkPermissions={{
                                objects: entityName,
                                actions: ["create", "update"]
                              }}
                            >
                              {hideSaveButton ? null : (
                                <Button
                                  title={saveMessage || messages.save}
                                  disabled={
                                    pristine || disableSaveChanges(form)
                                  }
                                  onPress={() => {
                                    this.setState({ loadingSave: true });
                                    handleSubmit({ validationContext });
                                  }}
                                />
                              )}
                            </SecuredComponent>
                          )}
                        </ScrollView>
                      )}
                    </FormSpy>
                  )}
                >
                  {inspectArgs => children(inspectArgs)}
                </ImmutableInspect>
              );
            }}
          </ListInspectContext.Consumer>
        )}
      </ToastContext.Consumer>
    );
  }
}

MutableInspect.defaultProps = {
  refetchQueries: []
};

export default withRouter(MutableInspect);
